/**
 * PaymentCancelled.js — صفحة إلغاء الدفع لمنصة CloudGM Customer
 *
 * تُستقبل من بوابات الدفع عند إلغاء المستخدم للدفع أو انقطاعه.
 * Query params (اختيارية):
 *   ?inv=CGB-000023
 *   ?reason=user_cancelled|timeout|declined
 *   ?lang=ar|en
 */
import { useSearchParams, Link, useNavigate } from 'react-router-dom';
import { XCircle, Home, RotateCcw, ArrowLeft, ArrowRight, HelpCircle } from 'lucide-react';
import { useLanguage } from '../config/language';
import { useTheme } from '../config/theme';

const T = {
  title:       { ar: 'تم إلغاء عملية الدفع',                    en: 'Payment Cancelled' },
  subtitle:    { ar: 'لم تكتمل عملية الدفع — لن يُخصم أي مبلغ.', en: 'The payment was not completed — no amount will be charged.' },
  invoiceNo:   { ar: 'رقم الفاتورة',                            en: 'Invoice Number' },
  reason:      { ar: 'السبب',                                   en: 'Reason' },
  reasonUser:  { ar: 'تم الإلغاء من قبل المستخدم',              en: 'Cancelled by user' },
  reasonTimeout:{ ar: 'انتهت المهلة الزمنية',                  en: 'Session timed out' },
  reasonDeclined:{ ar: 'رُفضت المعاملة',                       en: 'Transaction declined' },
  retry:       { ar: 'إعادة المحاولة',                           en: 'Try Again' },
  home:        { ar: 'العودة للرئيسية',                        en: 'Back to Home' },
  needHelp:    { ar: 'تحتاج مساعدة؟',                           en: 'Need help?' },
  contactSupport: { ar: 'تواصل مع الدعم',                      en: 'Contact Support' },
};

export default function PaymentCancelled() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { language } = useLanguage();
  const { isDarkMode } = useTheme();
  const langParam = params.get('lang');
  const isAr = (langParam === 'ar') || (!langParam && language === 'ar');
  const dir = isAr ? 'rtl' : 'ltr';
  const t = (k) => T[k][isAr ? 'ar' : 'en'];

  const invoiceId = params.get('inv') || params.get('invoice_id');
  const reason = params.get('reason');
  const reasonText =
    reason === 'user_cancelled' ? t('reasonUser') :
    reason === 'timeout'        ? t('reasonTimeout') :
    reason === 'declined'       ? t('reasonDeclined') :
    null;

  const BLUE = '#3B82F6';
  const bg = isDarkMode ? 'bg-[#0a0a0a]' : 'bg-[#F8FAFC]';
  const card = isDarkMode ? 'bg-[#141414] border border-white/10' : 'bg-white border border-[#E2E8F0] shadow-sm';
  const textMain = isDarkMode ? 'text-white' : 'text-[#0F172A]';
  const textMuted = isDarkMode ? 'text-gray-400' : 'text-[#64748B]';
  const border = isDarkMode ? 'border-white/10' : 'border-[#E2E8F0]';
  const BackArrow = isAr ? ArrowRight : ArrowLeft;

  return (
    <div className={`min-h-screen ${bg}`} dir={dir} data-testid="payment-cancelled-page">
      <div className="fixed inset-0 pointer-events-none">
        <div className={`absolute top-20 start-1/4 w-[400px] h-[400px] ${isDarkMode ? 'bg-red-500/10' : 'bg-red-500/5'} rounded-full blur-[100px]`} />
      </div>

      <section className="relative pt-10 pb-16">
        <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className={`rounded-2xl overflow-hidden ${card}`}>
            {/* Header */}
            <div className="p-8 md:p-12 text-center" style={{ background: 'linear-gradient(180deg, rgba(239,68,68,0.15), transparent)' }}>
              <div className="mx-auto mb-4 w-20 h-20 rounded-full flex items-center justify-center bg-red-500/20">
                <XCircle className="h-12 w-12 text-red-500" />
              </div>
              <h1 className={`text-2xl sm:text-3xl font-bold mb-2 ${textMain}`} data-testid="payment-cancelled-title">
                {t('title')}
              </h1>
              <p className={`text-sm ${textMuted} max-w-md mx-auto`}>{t('subtitle')}</p>
            </div>

            {/* Details */}
            {(invoiceId || reasonText) && (
              <div className={`p-6 md:p-8 border-y ${border} space-y-3 text-sm`}>
                {invoiceId && (
                  <div className="flex justify-between items-center">
                    <span className={textMuted}>{t('invoiceNo')}</span>
                    <span className={`font-medium ${textMain}`} dir="ltr" data-testid="cancelled-invoice-no">{invoiceId}</span>
                  </div>
                )}
                {reasonText && (
                  <div className="flex justify-between items-center">
                    <span className={textMuted}>{t('reason')}</span>
                    <span className={`font-medium ${textMain}`}>{reasonText}</span>
                  </div>
                )}
              </div>
            )}

            {/* Actions */}
            <div className={`p-6 md:p-8 flex flex-col sm:flex-row items-center gap-3`}>
              <button
                onClick={() => navigate(-1)}
                className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-6 py-3 rounded-xl text-white font-medium transition-colors flex-1"
                style={{ backgroundColor: BLUE }}
                data-testid="cancelled-retry-btn"
              >
                <RotateCcw className="h-4 w-4" />
                {t('retry')}
              </button>
              <Link
                to="/"
                className={`w-full sm:w-auto inline-flex items-center justify-center gap-2 px-6 py-3 rounded-xl font-medium transition-colors ${
                  isDarkMode ? 'bg-white/10 hover:bg-white/15 text-white' : 'bg-slate-100 hover:bg-slate-200 text-[#0F172A]'
                }`}
                data-testid="cancelled-home-btn"
              >
                <Home className="h-4 w-4" />
                {t('home')}
                <BackArrow className="h-4 w-4" />
              </Link>
            </div>

            {/* Help footer */}
            <div className={`p-4 border-t ${border} flex items-center justify-center gap-2 text-sm`}>
              <HelpCircle className={`h-4 w-4 ${textMuted}`} />
              <span className={textMuted}>{t('needHelp')}</span>
              <Link to="/support" className="font-medium hover:underline" style={{ color: BLUE }} data-testid="cancelled-support-link">
                {t('contactSupport')}
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
